import {
  AutopilotTaskDto,
  TaskBlockerBlockerType,
} from '@urbanx/agx.activities.client'
import {
  StaffGetContractSettlementResponse,
  UploadedDocument,
  useStaffGetContractSettlement,
} from '@urbanx/agx.campaigns.client'
import {
  AgxSpacer,
  AgxTypography,
  Icon_CheckmarkCircleOutline,
  Icon_Clock,
  AgxEmptyState,
} from '@urbanx/agx.ui-components2'
import { JSX, ReactNode, useState } from 'react'
import { formatDateOnly } from 'utils/date'
import { BlockerLayout } from './BlockerLayout'
import MarkedAsSettled from './BlockerModals/MarkAsSettled'

interface SettlementPendingBlockerProps {
  task: AutopilotTaskDto
  children: ReactNode
  onTasksInvalidated?: () => void
}

type ContractSettlementWithConfirmations = StaffGetContractSettlementResponse & {
  vendorSettlementConfirmation?: UploadedDocument
  buyerSettlementConfirmation?: UploadedDocument
}

interface SettlementConfirmationStatusRowProps {
  id: string
  label: string
  isComplete: boolean
}

const SettlementConfirmationStatusRow = ({
  id,
  label,
  isComplete,
}: SettlementConfirmationStatusRowProps): JSX.Element => (
  <AgxSpacer direction="horizontal" contentAlignment="center" gap="spacing-sm">
    {isComplete ? (
      <Icon_CheckmarkCircleOutline
        width={16}
        height={16}
        color="var(--fg-success)"
      />
    ) : (
      <Icon_Clock width={16} height={16} color="var(--text-tertiary)" />
    )}
    <AgxTypography
      id={id}
      type="body_ui"
      size="small"
      color={isComplete ? 'var(--text-success)' : 'var(--text-tertiary)'}
    >
      {label}
    </AgxTypography>
  </AgxSpacer>
)

const SettlementConfirmationDescription = ({
  settlement,
}: {
  settlement?: ContractSettlementWithConfirmations
}): JSX.Element => {
  const settlementDateText = formatDateOnly(settlement?.actualSettlementDate)

  return (
    <AgxSpacer
      direction="vertical"
      contentAlignment="center"
      gap="spacing-ml"
    >
      <SettlementConfirmationStatusRow
        id="settlementDate"
        isComplete={!!settlementDateText}
        label={
          settlementDateText
            ? `Settlement date: ${settlementDateText}`
            : 'Settlement date pending'
        }
      />
      <SettlementConfirmationStatusRow
        id="vendorConfirmation"
        isComplete={!!settlement?.vendorSettlementConfirmation}
        label={
          settlement?.vendorSettlementConfirmation
            ? 'Vendor confirmation received'
            : 'Vendor confirmation pending'
        }
      />
      <SettlementConfirmationStatusRow
        id="buyerConfirmation"
        isComplete={!!settlement?.buyerSettlementConfirmation}
        label={
          settlement?.buyerSettlementConfirmation
            ? 'Buyer confirmation received'
            : 'Buyer confirmation pending'
        }
      />
    </AgxSpacer>
  )
}

export const SettlementPendingBlocker = ({
  task,
  children,
  onTasksInvalidated,
}: SettlementPendingBlockerProps): JSX.Element => {
  const blockerTypes = new Set((task.blockers ?? []).map((b) => b.blockerType))
  const hasNotMarkedAsSettledBlocker = blockerTypes.has(
    TaskBlockerBlockerType.NotMarkedAsSettled
  )
  const { data: contractSettlement } = useStaffGetContractSettlement(
    task.campaignId ?? '',
    task.contractId ?? '',
    hasNotMarkedAsSettledBlocker && !!task.campaignId && !!task.contractId
  )
  const settlement =
    contractSettlement as ContractSettlementWithConfirmations | undefined
  const [isOpen, setIsOpen] = useState(false)
  if (hasNotMarkedAsSettledBlocker) {
    return (
      <BlockerLayout task={task}>
        <MarkedAsSettled
          task={task}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          onTasksInvalidated={onTasksInvalidated}
        />
        <AgxEmptyState
          variant="advanced"
          label="Pending Settlement Confirmation"
          glyphType={'locked'}
          actionButtons={[
            {
              label: 'Mark as Settled',
              isPrimary: true,
              onClick: () => setIsOpen(true),
              order: 1,
            },
          ]}
          description={<SettlementConfirmationDescription settlement={settlement} />}
        ></AgxEmptyState>
      </BlockerLayout>
    )
  }

  return <>{children}</>
}
